import React, { useState, useEffect, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import Swal from 'sweetalert2';
import AssignTaskToDateModal from './AssignTaskToDateModal';
import ScheduleEntryDetailModal from './ScheduleEntryDetailModal';
import SpecialNotesModal from './SpecialNotesModal';
import SpecialNoteDetailModal from './SpecialNoteDetailModal';
import axiosInstance from '../api/axiosInstance';
import branchService from '../api/branchService'; // branchService 임포트
import '../App.css';
import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import { tasksState, supervisorsListState, scheduleEntriesState, supervisorAssignmentDaysState } from '../jotai/atoms';
import { useAuth } from '../context/AuthContext';
import './SchedulePageButtons.css';

function SchedulePage() {
  const tasks = useAtomValue(tasksState);
  const supervisors = useAtomValue(supervisorsListState);
  const [scheduleEntries, setScheduleEntries] = useAtom(scheduleEntriesState);
  const setTasks = useSetAtom(tasksState);
  const setSupervisorAssignmentDays = useSetAtom(supervisorAssignmentDaysState);
  const { user } = useAuth();

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isSpecialNotesModalOpen, setIsSpecialNotesModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedScheduleEntry, setSelectedScheduleEntry] = useState(null);
  const [isScheduleEntryDetailEditMode, setIsScheduleEntryDetailEditMode] = useState(false);
  const [selectedDates, setSelectedDates] = useState([new Date().toISOString().split('T')[0]]);
  const [specialNotes, setSpecialNotes] = useState([]);
  const [viewDates, setViewDates] = useState({ start: null, end: null });
  const [isSpecialNoteDetailModalOpen, setIsSpecialNoteDetailModalOpen] = useState(false);
  const [selectedSpecialNote, setSelectedSpecialNote] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [branches, setBranches] = useState([]); // 지사 목록 상태
  const [selectedBranch, setSelectedBranch] = useState(''); // 선택된 지사 상태

  const fetchTasks = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/tasks');
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  }, [setTasks]);

  const fetchBranches = useCallback(async () => {
    try {
      const response = await branchService.getBranches();
      setBranches(response.data);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  }, []);

  const fetchScheduleEntries = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/schedule-entries');
      setScheduleEntries(response.data);
    } catch (error) {
      console.error('Error fetching schedule entries:', error);
    }
  }, [setScheduleEntries]);

  const fetchSupervisorAssignmentDays = useCallback(async () => {
    try {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth() + 1;
      const response = await axiosInstance.get(`/schedule-entries/assignment-days/${year}/${month}`);
      setSupervisorAssignmentDays(response.data);
    } catch (error) {
      console.error('Error fetching supervisor assignment days:', error);
    }
  }, [setSupervisorAssignmentDays]);

  const fetchSpecialNotes = useCallback(async (startDate, endDate) => {
    if (!startDate || !endDate) {
      setSpecialNotes([]);
      return;
    }
    try {
      const response = await axiosInstance.get(`/special-notes/range?startDate=${startDate.toISOString().split('T')[0]}&endDate=${endDate.toISOString().split('T')[0]}`);
      setSpecialNotes(response.data);
    } catch (error) {
      console.error('Error fetching special notes:', error);
    }
  }, [setSpecialNotes]);

  useEffect(() => {
    fetchTasks();
    fetchBranches(); // 지사 목록 가져오기
    fetchScheduleEntries();
    fetchSupervisorAssignmentDays();
  }, [fetchTasks, fetchBranches, fetchScheduleEntries, fetchSupervisorAssignmentDays]);

  useEffect(() => {
    if (viewDates.start && viewDates.end) {
      fetchSpecialNotes(viewDates.start, viewDates.end);
    }
  }, [viewDates, fetchSpecialNotes]);

const searchedScheduleEntries = scheduleEntries.filter(entry => {
  const task = entry.task;
  if (!task) return false;

  // 지사 필터링
  const branchMatch = !selectedBranch || task.branch === selectedBranch;

// 검색어 필터링
const searchMatch = (() => {
  const trimmedTerm = searchTerm.trim();
  if (!trimmedTerm) return true;

  // "준공" 검색 → 준공 배치(취소선 표시되는 일정)만 표시
  if (trimmedTerm === '준공') {
    return entry.projectType === '준공';
  }

  // "공사중" 검색 → 준공 배치가 아닌 일정만 표시
  if (trimmedTerm === '공사중') {
    return entry.projectType !== '준공';
  }
    // 그 외에는 기존 검색
    const lowerCaseSearchTerm = trimmedTerm.toLowerCase();
    const title = `${task.name} (${entry.assignedManagers})`;
    let assistantsString = '';
    if (Array.isArray(entry.assignedAssistants)) {
      assistantsString = entry.assignedAssistants.join(' ');
    } else if (typeof entry.assignedAssistants === 'string') {
      assistantsString = entry.assignedAssistants;
    }
    return (
      title.toLowerCase().includes(lowerCaseSearchTerm) ||
      (task.number || '').toLowerCase().includes(lowerCaseSearchTerm) ||
      (task.mainTaskNumber || '').toLowerCase().includes(lowerCaseSearchTerm) ||
      assistantsString.toLowerCase().includes(lowerCaseSearchTerm) ||
      (task.branch && task.branch.toLowerCase().includes(lowerCaseSearchTerm)) ||
      (task.type && task.type.toLowerCase().includes(lowerCaseSearchTerm))
    );
  })();

  return branchMatch && searchMatch;
});

  // Group consecutive schedule entries for the same task
  const groupedTaskEvents = {};

  searchedScheduleEntries.filter(entry => entry.task).forEach(entry => {
    const taskId = entry.task.id;
    if (!groupedTaskEvents[taskId]) {
      groupedTaskEvents[taskId] = [];
    }
    groupedTaskEvents[taskId].push(entry);
  });

  const mergedScheduleEvents = [];
  for (const taskId in groupedTaskEvents) {
    const entriesForTask = groupedTaskEvents[taskId].sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));

    if (entriesForTask.length === 0) continue;

    let currentGroup = [entriesForTask[0]];
    for (let i = 1; i < entriesForTask.length; i++) {
      const prevDate = new Date(currentGroup[currentGroup.length - 1].scheduledDate);
      const currentDate = new Date(entriesForTask[i].scheduledDate);
      
      if (currentDate.getTime() === prevDate.getTime() + (24 * 60 * 60 * 1000)) {
        currentGroup.push(entriesForTask[i]);
      } else {
        mergedScheduleEvents.push(createMergedEvent(currentGroup));
        currentGroup = [entriesForTask[i]];
      }
    }
    mergedScheduleEvents.push(createMergedEvent(currentGroup));
  }

  function createMergedEvent(entries) {
    const firstEntry = entries[0];
    const lastEntry = entries[entries.length - 1];
    const endDate = new Date(lastEntry.scheduledDate);
    endDate.setDate(endDate.getDate() + 1);

    let eventColor = '#3788d8';
    if (lastEntry.deploymentStatus === '배치계획') {
      eventColor = '#FFD700';
    } else if (lastEntry.deploymentStatus === '배치지시') {
      eventColor = '#007bff';
    } else if (lastEntry.deploymentStatus === '배치완료') {
      if (firstEntry.projectType === '준공') {
        eventColor = '#add8e6';
      } else {
        eventColor = '#28a745';
      }
    }

    const isCompletedProject = firstEntry.projectType === '준공';
    const classNames = isCompletedProject ? ['strikethrough-event'] : [];

    let prefix = '';
    if (firstEntry.task.type === '단가' && firstEntry.task.branch) {
      prefix = `(${firstEntry.task.branch.substring(0, 1)}) `;
    } else if (firstEntry.task.type === '총가') {
      prefix = '(총가) ';
    }
    const eventTitle = `${prefix}${firstEntry.task.name} (${firstEntry.assignedManagers})`;

    return {
      id: `task-${firstEntry.task.id}-${firstEntry.scheduledDate}-${lastEntry.scheduledDate}`,
      title: eventTitle,
      start: firstEntry.scheduledDate,
      end: endDate.toISOString().split('T')[0],
      allDay: true,
      color: eventColor,
      textColor: 'black',
      classNames: classNames,
      extendedProps: {
        type: 'scheduleEntry',
        data: firstEntry,
        allEntries: entries
      }
    };
  }

  const searchedSpecialNotes = specialNotes.filter(note => {
    if (!searchTerm) {
      return true;
    }
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    const supervisor = supervisors.find(s => s.id === note.supervisorId);
    return supervisor && supervisor.name.toLowerCase().includes(lowerCaseSearchTerm);
  });

  const events = [
    ...mergedScheduleEvents,
    ...searchedSpecialNotes.map(note => {
      const supervisorName = supervisors.find(s => s.id === note.supervisorId)?.name || '알 수 없음';
      return {
        id: `note-${note.id}`,
        title: `${supervisorName} (${note.noteType})`,
        start: note.scheduledDate,
        allDay: true,
        color: 'red',
        textColor: 'black',
        extendedProps: {
          type: 'specialNote',
          data: note
        }
      };
    })
  ];

  const handleDateSelect = (arg) => {
    const dates = [];
    let currentDate = new Date(arg.start);
    while (currentDate < arg.end) {
      const year = currentDate.getFullYear();
      const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
      const day = currentDate.getDate().toString().padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    setSelectedDates(dates);
  };

  const openScheduleEntryDetailModal = (entry, isEditMode = false) => {
    setSelectedScheduleEntry(entry);
    setIsScheduleEntryDetailEditMode(isEditMode);
    setIsDetailModalOpen(true);
  };

  const closeScheduleEntryDetailModal = () => {
    setSelectedScheduleEntry(null);
    setIsScheduleEntryDetailEditMode(false);
    setIsDetailModalOpen(false);
  };

  const openSpecialNoteDetailModal = (note) => {
    setSelectedSpecialNote(note);
    setIsSpecialNoteDetailModalOpen(true);
  };

  const closeSpecialNoteDetailModal = () => {
    setSelectedSpecialNote(null);
    setIsSpecialNoteDetailModalOpen(false);
  };

  const handleEventClick = (clickInfo) => {
    if (clickInfo.event.extendedProps.type === 'scheduleEntry') {
      openScheduleEntryDetailModal(clickInfo.event.extendedProps.allEntries, false);
    }
  };

  const formatDateToDot = (dateString) => {
    if (!dateString) return '';
    return dateString.replace(/-/g, '.');
  };

  const formatDateRangeDisplay = (dates) => {
    if (!dates || dates.length === 0) {
      return '날짜를 선택하세요';
    }
    if (dates.length === 1) {
      return `${formatDateToDot(dates[0])} 일정`;
    }
    const sortedDates = [...dates].sort();
    const firstDate = sortedDates[0];
    const lastDate = sortedDates[sortedDates.length - 1];
    
    const formatShortDate = (dateString) => {
        if (!dateString) return '';
        return dateString.substring(2).replace(/-/g, '.');
    };

    return `${formatShortDate(firstDate)} ~ ${formatShortDate(lastDate)} (${dates.length})`;
  };

  const handleAssignExistingTask = async (taskId, assignedManagers, assignedAssistants, deploymentStatus, projectType) => {
    try {
      let hasDuplicate = false;
      for (const date of selectedDates) {
        const assignedTask = tasks.find(task => task.id === taskId);
        if (!assignedTask) {
          console.error(`Task with ID ${taskId} not found.`);
          continue;
        }
        const isDuplicate = scheduleEntries.some(entry =>
          entry.task.number === assignedTask.number && entry.scheduledDate === date && entry.task.id === assignedTask.id
        );

        if (isDuplicate) {
          Swal.fire({
            icon: 'warning',
            title: '중복 공사',
            text: `선택된 날짜 ${date}에 이미 해당 공사가 배치되어 있습니다.`, 
          });
          hasDuplicate = true;
          continue;
        }

        const scheduleEntryData = {
          taskId: taskId,
          scheduledDate: date,
          assignedManagers: assignedManagers,
          assignedAssistants: assignedAssistants.includes('없음') ? [] : assignedAssistants,
          deploymentStatus: deploymentStatus,
          projectType: projectType
        };
        await axiosInstance.post('/schedule-entries', scheduleEntryData);
      }
      fetchScheduleEntries();
      fetchSupervisorAssignmentDays();
      setIsAssignModalOpen(false);
      if (!hasDuplicate) {
        Swal.fire('성공', '감리원 배치가 완료되었습니다.', 'success');
      }
    } catch (error) {
      console.error('Error assigning existing task:', error);
      Swal.fire({
        icon: 'error',
        title: '오류',
        text: error.response?.data || '감리원 배치 중 오류가 발생했습니다.',
      });
    }
  };

  const handleCreateNewTask = async (newTaskData) => {
    try {
      const response = await axiosInstance.post('/tasks', newTaskData);
      const createdTask = response.data;

      let hasDuplicate = false;
      for (const date of selectedDates) {
        const isDuplicate = scheduleEntries.some(entry =>
          entry.task.displayNumber === createdTask.displayNumber && entry.scheduledDate === date
        );

        if (isDuplicate) {
          Swal.fire({
            icon: 'warning',
            title: '중복 공사',
            text: `선택된 날짜 ${date}에 이미 해당 공사가 배치되어 있습니다.`, 
          });
          hasDuplicate = true;
          continue;
        }

        const scheduleEntryData = {
          taskId: createdTask.id,
          scheduledDate: date,
          assignedManagers: newTaskData.managers,
          assignedAssistants: newTaskData.assistants.includes('없음') ? [] : newTaskData.assistants,
          deploymentStatus: '배치계획'
        };
        await axiosInstance.post('/schedule-entries', scheduleEntryData);
      }

      fetchScheduleEntries();
      fetchSupervisorAssignmentDays();
      setIsAssignModalOpen(false);
      if (!hasDuplicate) {
        Swal.fire('성공', '새 공사 생성 및 감리원 배치가 완료되었습니다.', 'success');
      }
    } catch (error) {
      console.error('Error creating new task or schedule entry:', error);
      Swal.fire({
        icon: 'error',
        title: '오류',
        text: error.response?.data || '새 공사 생성 및 감리원 배치 중 오류가 발생했습니다.',
      });
    }
  };

  const handleUpdateScheduleEntry = async (updatedEntry) => {
    try {
      const dataToSend = {
        id: updatedEntry.id,
        taskId: updatedEntry.taskId,
        scheduledDate: updatedEntry.scheduledDate,
        assignedManagers: updatedEntry.assignedManagers,
        assignedAssistants: updatedEntry.assignedAssistants.includes('없음') ? [] : updatedEntry.assignedAssistants,
        deploymentStatus: updatedEntry.deploymentStatus,
        projectType: updatedEntry.projectType
      };
      await axiosInstance.put(`/schedule-entries/${updatedEntry.id}`, dataToSend);

      if (updatedEntry.projectType === '준공' && updatedEntry.deploymentStatus === '배치완료') {
        const taskToUpdate = tasks.find(t => t.id === updatedEntry.taskId);
        if (taskToUpdate && taskToUpdate.taskStatus === '공사중') {
          try {
            const updatedTaskData = { ...taskToUpdate, taskStatus: '준공' };
            await axiosInstance.put(`/tasks/${taskToUpdate.id}`, updatedTaskData);
          } catch (taskError) {
            console.error('Error updating task status to "준공":', taskError);
            Swal.fire({
              icon: 'error',
              title: '공사 상태 업데이트 오류',
              text: '일정의 상태는 변경되었으나, 해당 공사의 상태를 "준공"으로 변경하는 데 실패했습니다.',
            });
          }
        }
      }

      if (updatedEntry.deploymentStatus === '배치계획' && updatedEntry.taskId) {
        try {
          await axiosInstance.put(`/work-orders/update-status-by-task-and-date`, null, {
            params: {
              taskId: updatedEntry.taskId,
              scheduledDate: updatedEntry.scheduledDate,
              newStatus: 'review'
            }
          });
        } catch (workOrderError) {
          console.error('Error updating related WorkOrder status:', workOrderError);
        }
      } else if (updatedEntry.deploymentStatus === '배치완료' && updatedEntry.taskId) {
        try {
          await axiosInstance.put(`/work-orders/update-status-by-task-and-date`, null, {
            params: {
              taskId: updatedEntry.taskId,
              scheduledDate: updatedEntry.scheduledDate,
              newStatus: 'done'
            }
          });
        } catch (workOrderError) {
          console.error('Error updating related WorkOrder status:', workOrderError);
        }
      }

      fetchTasks();
      fetchScheduleEntries();
      closeScheduleEntryDetailModal();
      Swal.fire('성공', '일정 항목이 성공적으로 수정되었습니다.', 'success');
    } catch (error) {
      console.error('Error updating schedule entry:', error);
      Swal.fire({
        icon: 'error',
        title: '오류',
        text: error.response.data,
      });
    }
  };

  const handleDeleteScheduleEntry = async (entryId) => {
    const result = await Swal.fire({
      title: '정말로 삭제하시겠습니까?',
      text: "이 일정 항목을 삭제하면 되돌릴 수 없습니다!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '예, 삭제합니다!',
      cancelButtonText: '취소',
    });

    if (result.isConfirmed) {
      try {
        const entryToDelete = scheduleEntries.find(entry => entry.id === entryId);
        if (!entryToDelete || !entryToDelete.task || !entryToDelete.task.id || !entryToDelete.scheduledDate) {
          console.error('Error: Task ID or Scheduled Date not found for schedule entry:', entryId);
          Swal.fire('오류!', '관련 공사 정보를 찾을 수 없습니다.', 'error');
          return;
        }
        const taskId = entryToDelete.task.id;
        const scheduledDate = entryToDelete.scheduledDate;

        await axiosInstance.delete(`/work-orders/by-task-and-date?taskId=${taskId}&assignmentDate=${scheduledDate}`);
        await axiosInstance.delete(`/schedule-entries/${entryId}`);
        setScheduleEntries(prev => prev.filter(entry => entry.id !== entryId));
        fetchSupervisorAssignmentDays();
        closeScheduleEntryDetailModal();
        Swal.fire('삭제됨!', '일정 항목 및 관련 작업 지시가 삭제되었습니다.', 'success');
      } catch (error) {
        console.error('Error deleting schedule entry or related work orders:', error);
        Swal.fire('오류!', '일정 항목 및 관련 작업 지시 삭제 중 오류가 발생했습니다.', 'error');
      }
    }
  };

  const handleUpdateSpecialNote = async (updatedNote) => {
    try {
      await axiosInstance.put(`/special-notes/${updatedNote.id}`, updatedNote);
      fetchSpecialNotes(viewDates.start, viewDates.end);
      closeSpecialNoteDetailModal();
      Swal.fire('성공', '특이사항이 성공적으로 수정되었습니다.', 'success');
    } catch (error) {
      console.error('Error updating special note:', error);
      Swal.fire({
        icon: 'error',
        title: '오류',
        text: error.response?.data || '특이사항 수정 중 오류가 발생했습니다.',
      });
    }
  };

  const handleDeleteSpecialNote = async (noteId) => {
    const result = await Swal.fire({
      title: '정말로 삭제하시겠습니까?',
      text: "이 특이사항을 삭제하면 되돌릴 수 없습니다!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '예, 삭제합니다!',
      cancelButtonText: '취소',
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/special-notes/${noteId}`);
        fetchSpecialNotes(viewDates.start, viewDates.end);
        closeSpecialNoteDetailModal();
        Swal.fire('삭제됨!', '특이사항이 삭제되었습니다.', 'success');
      } catch (error) {
        console.error('Error deleting special note:', error);
        Swal.fire('오류!', '특이사항 삭제 중 오류가 발생했습니다.', 'error'); 
      }
    }
  };

  const filteredScheduleEntries = selectedDates.length > 0
    ? searchedScheduleEntries.filter(entry => selectedDates.includes(entry.scheduledDate))
                    .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))
    : [];

  const plannedEntries = filteredScheduleEntries.filter(entry => entry.deploymentStatus === '배치계획');
  const instructedEntries = filteredScheduleEntries.filter(entry => entry.deploymentStatus === '배치지시');
  const completedEntries = filteredScheduleEntries.filter(entry => entry.deploymentStatus === '배치완료');

  const renderScheduleTable = (entries, status) => {
    if (entries.length === 0) {
      return <p>선택된 날짜에 {status}된 공사가 없습니다.</p>;
    }
    return (
      <table className="main-table schedule-entry-table">
        <thead>
          <tr>
            <th>날짜</th>
            <th>공사명</th>
            <th>책임</th>
            <th>보조</th>
            <th>지사</th>
            {user && user.role === 'ADMIN' && <th>관리</th>}
          </tr>
        </thead>
        <tbody>
          {entries.filter(entry => entry.task).map(entry => (
            <tr key={entry.id}>
              <td>
                {(() => {
                  const date = new Date(entry.scheduledDate);
                  const year = date.getFullYear().toString().slice(-2);
                  const month = (date.getMonth() + 1).toString().padStart(2, '0');
                  const day = date.getDate().toString().padStart(2, '0');
                  console.log(entry.task)
                  return `${year}.${month}.${day}`;
                })()}
              </td>
              <td onClick={() => openScheduleEntryDetailModal(entry, false)} style={{ cursor: 'pointer' }}>
                {entry.task.name}
              </td>
              <td onClick={() => openScheduleEntryDetailModal(entry, false)} style={{ cursor: 'pointer' }}>
                {entry.assignedManagers}
              </td>
              <td onClick={() => openScheduleEntryDetailModal(entry, false)} style={{ cursor: 'pointer' }}>
                {entry.assignedAssistants || '없음'}
              </td>
              {/* <td>{entry.deploymentStatus === "배치계획" ? "🟡" : entry.deploymentStatus === "배치지시" ? "🔵" : "🟢"}</td> */}
              <td>
              {entry.task.type === '단가' ? entry.task.branch.substring(0, 2) : "총가"}
              </td>
              {user && user.role === 'ADMIN' && (
                <td>
                  <div className="schedule-actions-container">
                    <button onClick={() => openScheduleEntryDetailModal(entry, true)} className="schedule-edit-button">
                      수정
                    </button>
                    <button onClick={() => handleDeleteScheduleEntry(entry.id)} className="schedule-delete-button">
                      삭제
                    </button>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const filteredSpecialNotes = selectedDates.length > 0
    ? specialNotes.filter(note => selectedDates.includes(note.scheduledDate))
                    .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))
    : [];

  return (
    <div className="container" style={{ marginTop: '20px' }}>
      <div className="schedule-page-layout">
        <div className="calendar-section">
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <input
              type="text"
              placeholder="공사명, 공사번호 등으로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flexGrow: 1, padding: '8px', boxSizing: 'border-box' }}
            />
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              style={{ padding: '8px', boxSizing: 'border-box' }}
            >
              <option value="">전체 지사</option>
              {branches.map(branch => (
                <option key={branch.id} value={branch.name}>{branch.name}</option>
              ))}
            </select>
          </div>
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            weekends={true}
            events={events}
            eventClick={handleEventClick}
            selectable={true}
            longPressDelay={0}
            select={handleDateSelect}
            datesSet={(dateInfo) => setViewDates({ start: dateInfo.start, end: dateInfo.end })}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,dayGridWeek,dayGridDay'
            }}
            locale="ko"
          />
        </div>
        <div className="selected-date-tasks">
          <div className="date-and-button-container">
            <h2>{formatDateRangeDisplay(selectedDates)}</h2>
            {selectedDates.length > 0 && user && user.role === 'ADMIN' && (
              <>
                <button onClick={() => setIsAssignModalOpen(true)} className="register-button"> 
                  감리원 배치
                </button>
                <button onClick={() => setIsSpecialNotesModalOpen(true)} className="register-button"> 
                  특이사항 등록
                </button>
              </>
            )}
          </div>

          {selectedDates.length > 0 && (
            <>
              <div className="schedule-list-section">
                <h3 style={{ textAlign: 'left' }}>배치계획 ({plannedEntries.length}건)</h3>
                {renderScheduleTable(plannedEntries, '배치계획')}
              </div>
              <div className="schedule-list-section">
                <h3 style={{ textAlign: 'left' }}>배치지시 ({instructedEntries.length}건)</h3>
                {renderScheduleTable(instructedEntries, '배치지시')}
              </div>
              <div className="schedule-list-section">
                <h3 style={{ textAlign: 'left' }}>배치완료 ({completedEntries.length}건)</h3>
                {renderScheduleTable(completedEntries, '배치완료')}
              </div>
            </>
          )}

          {selectedDates.length > 0 && filteredSpecialNotes.length > 0 && (
            <div className="special-notes-list-section">
              <h3 style={{ textAlign: 'left' }}>특이사항 ({filteredSpecialNotes.length}건)</h3>
              <table className="main-table special-notes-table">
                <thead>
                  <tr>
                    <th>날짜</th>
                    <th>감리원</th>
                    <th>유형</th>
                    {user && user.role === 'ADMIN' && <th>관리</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredSpecialNotes.map(note => (
                    <tr key={note.id} onClick={() => openSpecialNoteDetailModal(note)} style={{ cursor: 'pointer' }}>
                      <td>
                        {(() => {
                          const date = new Date(note.scheduledDate);
                          const year = date.getFullYear().toString().slice(-2);
                          const month = (date.getMonth() + 1).toString().padStart(2, '0');
                          const day = date.getDate().toString().padStart(2, '0');
                          return `${year}.${month}.${day}`;
                        })()}
                      </td>
                      <td>{supervisors.find(s => s.id === note.supervisorId)?.name || '알 수 없음'}</td>
                      <td>{note.noteType}</td>
                      {user && user.role === 'ADMIN' && (
                        <td>
                          <div className="schedule-actions-container">
                            <button 
                              onClick={(e) => { e.stopPropagation(); openSpecialNoteDetailModal(note); }} 
                              className="schedule-edit-button"
                            >
                              수정
                            </button>
                            <button 
                              onClick={(e) => { e.stopPropagation(); handleDeleteSpecialNote(note.id); }} 
                              className="schedule-delete-button"
                            >
                              삭제
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {selectedDates.length > 0 && filteredSpecialNotes.length === 0 && (
            <p>선택된 날짜에 등록된 특이사항이 없습니다.</p>
          )}
        </div>
      </div>

      <AssignTaskToDateModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        selectedDates={selectedDates}
        tasks={tasks}
        supervisors={supervisors}
        onAssignTask={handleAssignExistingTask}
        onCreateNewTask={handleCreateNewTask}
        assignedEntries={filteredScheduleEntries}
      />

      {selectedScheduleEntry && (
        <ScheduleEntryDetailModal
          isOpen={isDetailModalOpen}
          onClose={closeScheduleEntryDetailModal}
          scheduleEntry={selectedScheduleEntry}
          onUpdateScheduleEntry={handleUpdateScheduleEntry}
          onDeleteScheduleEntry={handleDeleteScheduleEntry}
          supervisors={supervisors}
          isEditMode={isScheduleEntryDetailEditMode}
          assignedEntries={filteredScheduleEntries}
          user={user}
          fetchScheduleEntries={fetchScheduleEntries}
        />
      )}

      <SpecialNotesModal
        isOpen={isSpecialNotesModalOpen}
        onClose={() => setIsSpecialNotesModalOpen(false)}
        selectedDates={selectedDates}
        supervisors={supervisors}
        onSaveSuccess={() => fetchSpecialNotes(viewDates.start, viewDates.end)}
      />

      {selectedSpecialNote && (
        <SpecialNoteDetailModal
          isOpen={isSpecialNoteDetailModalOpen}
          onClose={closeSpecialNoteDetailModal}
          specialNote={selectedSpecialNote}
          supervisors={supervisors}
          onUpdateSpecialNote={handleUpdateSpecialNote}
          onDeleteSpecialNote={handleDeleteSpecialNote}
        />
      )}
    </div>
  );
}

export default SchedulePage;

